import { useParams } from "react-router-dom"
import { Skeleton } from "@chakra-ui/react"
import { useAsset } from "../hooks/useAsset"
import ReportWithPeriodControls from "@hydrologiq/shared/reporting/ReportWithPeriodControls"
import useEntityDataRange from "@hydrologiq/shared/reporting/useEntityDataRange"
import type { ReportScopeString } from "@hydrologiq/shared/reporting/useReport"

/**
 * The unified entity performance report for an asset (KPIs + graphs + wide
 * table), served on the `reporting` route. The legacy CSV/download reporting
 * lives separately on the `download` route (AssetUsage).
 *
 * A hybrid asset reports at the aggregated "hybrid" scope (parent + children);
 * every other class reports at the single-asset "asset" scope.
 *
 * The cadence/period selectors are bounded to the asset's active life via
 * {@link useEntityDataRange}, which fetches the `{ earliest, latest }` bounds
 * from the backend `getEntityDataRange` resolver (`earliest` → `lowerBound`,
 * `latest` → `anchorEnd`). Raw ISO strings are passed through; the wrapper snaps
 * them to day/week/month edges when building the options (see periodOptions).
 */
const AssetReport = () => {
  const { assetId = "" } = useParams()
  const { asset, loading } = useAsset(assetId)

  const scope: ReportScopeString = asset?.class?.toLowerCase() === "hybrid" ? "hybrid" : "asset"

  // The available window is owned by the backend resolver and consumed via the
  // shared hook (skips while assetId is empty).
  const { range } = useEntityDataRange(scope, assetId)

  if (loading || !asset) {
    return <Skeleton height="100%" width="100%" />
  }

  return (
    <ReportWithPeriodControls
      scope={scope}
      entityId={asset.id}
      entityName={asset.name}
      anchorEnd={range.latest}
      lowerBound={range.earliest}
    />
  )
}

export default AssetReport
